/**
 * BUILD A QUOTE — CONFIGURATION
 * ------------------------------------------------------------------
 * Package prices, included radiuses, add-on coupons, the extra-mile rate,
 * and add-on prices all live here. The quote page AND the server-side
 * price check both read this file, so one change updates both.
 *
 * Prices mirror the approved packages on the homepage. Never invent a
 * price — anything without an approved price uses `null` and is shown as
 * "Custom quote required".
 */

import { MONTHLY_PLANS, PLAN_FULL_SUPPORT, PLAN_HOSTING, PLAN_HOSTING_MGMT, type MonthlyPlan } from "@/lib/pricing/monthly-plans";
export { MONTHLY_PLANS };
export type QuotePlanKey = MonthlyPlan["key"];
export const MONTHLY_HOSTING_FROM = PLAN_HOSTING.dollars; // required management & hosting plan

/** Radius charges: first 80 mi included; miles 81–100 at $10/mi; every mile over 100 at $25/mi. */
export const EXTRA_MILE_RATE = 10;
export const EXTRA_MILE_RATE_OVER_100 = 25;
export const MIN_RADIUS = 1;
export const MAX_RADIUS = 250;

/**
 * OWNER CONTROLS — EXTENDED COVERAGE PLAN
 * Service areas larger than the threshold require the monthly plan below.
 * The rule begins at threshold + 1 mile (171 mi with the 170 default).
 */
export const EXTENDED_COVERAGE_THRESHOLD = 170; // miles; rule applies ABOVE this
export const EXTENDED_COVERAGE_MONTHLY = PLAN_FULL_SUPPORT.dollars; // required $/month plan
export const EXTENDED_COVERAGE_NAME = "Full Support Plan";
export const EXTENDED_COVERAGE_DESCRIPTION =
  `Service areas larger than 170 miles require the Full Support plan at $${PLAN_FULL_SUPPORT.dollars}/month for continued hosting, support, updates, and management.`;

/**
 * OWNER CONTROLS — FEATURE PLAN REQUIREMENT
 * Quotes that include any of these add-ons require the monthly plan below.
 */
export const FEATURE_PLAN_ADDONS = ["payments", "booking"]; // add-on ids that trigger the rule
export const FEATURE_PLAN_MONTHLY = PLAN_HOSTING_MGMT.dollars; // required $/month plan
export const FEATURE_PLAN_NAME = "Hosting + Management Plan";
export const FEATURE_PLAN_DESCRIPTION =
  `Online payments and online booking require the Hosting + Management plan at $${PLAN_HOSTING_MGMT.dollars}/month for payment-system support, booking maintenance, and ongoing management.`;

export type QuotePackage = {
  id: string;
  name: string;
  price: number;
  priceLabel: string;
  pages: string;
  radius: number; // included radius in miles
  includes: string[];
  /** Stable add-on IDs already included — hidden from the add-on grid and never charged. */
  includedAddOns: string[];
  monthly: number;
  /** Credit toward eligible one-time add-ons. */
  coupon: number;
  /** Only the top package can buy extra miles; smaller radiuses pick a smaller package. */
  allowsExtraMiles?: boolean;
};

/** Radius-based packages, smallest first. The radius picks the package automatically. */
export const QUOTE_PACKAGES: QuotePackage[] = [
  { id: "PKG_01", includedAddOns: ["local_seo", "gallery", "admin"], name: "MVP", price: 299, priceLabel: "$299", pages: "Up to 3 service-area pages", radius: 5, monthly: 50, coupon: 0,
    includes: ["Mobile optimization", "3 local service-area pages", "100 SEO score", "AI integration & organic search optimization"] },
  { id: "PKG_02", includedAddOns: ["local_seo", "gallery", "admin"], name: "Momentum", price: 599, priceLabel: "$599", pages: "Up to 10 service-area pages", radius: 10, monthly: 50, coupon: 0,
    includes: ["Everything in MVP", "Photo gallery", "Owner login", "AI integration & organic search optimization"] },
  { id: "PKG_03", includedAddOns: ["local_seo", "gallery", "admin"], name: "Authority", price: 899, priceLabel: "$899", pages: "Up to 20 service-area pages", radius: 20, monthly: 50, coupon: 0,
    includes: ["Everything in Momentum", "Expanded local SEO targeting", "Wider regional reach", "AI integration & organic search optimization"] },
  { id: "PKG_04", includedAddOns: ["local_seo", "gallery", "admin"], name: "Elevate", price: 1299, priceLabel: "$1,299", pages: "Up to 40 service-area pages", radius: 40, monthly: 50, coupon: 250,
    includes: ["Everything in Authority", "40-mile regional coverage", "AI integration & organic search optimization", "$250 toward add-ons"] },
  { id: "PKG_05", includedAddOns: ["local_seo", "gallery", "admin"], name: "Dominion", price: 1999, priceLabel: "$1,999", pages: "Up to 160 pages", radius: 80, monthly: 50, coupon: 500, allowsExtraMiles: true,
    includes: ["Everything in Elevate", "80-mile market saturation", "AI integration & organic search optimization", "$500 toward add-ons"] },
];

export type AddOn = {
  id: string;
  name: string;
  description: string;
  /** one-time dollars; null = custom quote; 0 = included */
  oneTime: number | null;
  /** monthly dollars when applicable */
  monthly?: number | null;
  couponEligible: boolean;
};

export const ADD_ONS: AddOn[] = [
  { id: "booking", name: "Online booking", description: "Customers pick a time and book right on your site.", oneTime: 500, couponEligible: true },
  { id: "payments", name: "Online payment processing", description: "Accept secure online payments through Stripe or Square.", oneTime: 500, couponEligible: true },
  { id: "customer_login", name: "Customer login", description: "A private area where your customers sign in.", oneTime: 1000, couponEligible: true },
  { id: "docusign", name: "DocuSign document setup", description: "Send contracts and agreements for e-signature.", oneTime: 200, couponEligible: true },
  { id: "admin", name: "Owner/admin dashboard", description: "Log in to update your own photos and content.", oneTime: 0, couponEligible: false },
  { id: "local_seo", name: "Local SEO", description: "Set up to show up in local Google searches.", oneTime: 0, couponEligible: false },
  { id: "gallery", name: "Photo gallery", description: "Show off your past jobs.", oneTime: 0, couponEligible: false },
  { id: "extra_pages", name: "Additional pages", description: "More pages beyond what your package includes.", oneTime: null, couponEligible: true },
  { id: "extra_areas", name: "Additional service-area pages", description: "More town pages to rank in more places.", oneTime: null, couponEligible: true },
  { id: "crm", name: "CRM", description: "Track every lead and customer in one place.", oneTime: null, couponEligible: true },
  { id: "store", name: "Store", description: "Sell products online.", oneTime: null, couponEligible: true },
  { id: "ai_front_desk", name: "AI Front Desk", description: "AI answers your calls and books jobs 24/7.", oneTime: null, couponEligible: true },
  { id: "chatbot", name: "Website chatbot", description: "Answers questions on your site day and night.", oneTime: null, couponEligible: true },
  { id: "follow_up", name: "Automated follow-up", description: "Texts and emails that follow up for you.", oneTime: null, couponEligible: true },
  { id: "reviews", name: "Review requests", description: "Automatically ask happy customers for reviews.", oneTime: null, couponEligible: true },
  { id: "analytics", name: "Analytics", description: "See who visits and what they do.", oneTime: null, couponEligible: true },
  { id: "integrations", name: "Custom integrations", description: "Connect the tools you already use.", oneTime: null, couponEligible: true },
];

export const TIMEFRAMES = ["As soon as possible", "Within 1 month", "1–3 months", "3+ months", "Just exploring"];
export const CONTACT_METHODS = ["Phone call", "Text", "Email"];

export type PaymentProcessor = "stripe" | "square" | "both";
export type PaymentAccount = "existing" | "new";
export type PaymentChoice = { processor: PaymentProcessor | null; account: PaymentAccount | null };
/** Owner-editable (CRM settings) — defaults used until settings load. */
export type PaymentSettings = { setupFee: number; setupCouponEligible: boolean; setupDescription?: string };
export const PAYMENT_SETUP_SERVICE_NAME = "Account Setup & Payment Integration";
export const DEFAULT_PAYMENT_SETUP_DESCRIPTION = "Setup and integration of a new Stripe or Square payment account with the customer's website.";
export const DEFAULT_PAYMENT_SETTINGS: PaymentSettings = { setupFee: 750, setupCouponEligible: false, setupDescription: DEFAULT_PAYMENT_SETUP_DESCRIPTION };
export const PROCESSOR_LABEL: Record<PaymentProcessor, string> = { stripe: "Stripe", square: "Square", both: "Stripe + Square" };

export type QuoteInput = { radius: number; addOns: string[]; payment?: PaymentChoice; paymentSettings?: PaymentSettings; plan?: QuotePlanKey };

export type QuoteResult = {
  pkg: QuotePackage;
  radius: number;
  includedRadius: number;
  additionalMiles: number;
  additionalRadiusCost: number;
  packageTotal: number;
  addOnLines: { id: string; name: string; oneTime: number; monthly: number; couponEligible: boolean }[];
  customItems: string[];
  addOnSubtotal: number;
  eligibleSubtotal: number;
  coupon: number;
  couponApplied: number;
  couponRemaining: number;
  addOnsAfterCoupon: number;
  monthlyAddOns: number;
  oneTimeTotal: number;
  monthlyTotal: number;
  areaSqMi: number;
  /** True when radius > EXTENDED_COVERAGE_THRESHOLD — the required plan applies. */
  extendedCoverage: boolean;
  /** True when payments or booking is selected — the Hosting + Management plan applies. */
  featurePlanRequired: boolean;
  /** Monthly plan floor actually charged (never below any required minimum). */
  requiredMonthly: number;
  /** Monthly plan actually applied (after required upgrades). */
  plan: MonthlyPlan;
  /** True when the customer's chosen plan was bumped up by a rule. */
  planUpgraded: boolean;
  paymentSetupRequired: boolean;
  includedAddOnIds: string[];
  purchasedAddOnIds: string[];
  paymentSetupFee: number;
};

export const packageForRadius = (radius: number) =>
  QUOTE_PACKAGES.find((p) => p.radius >= radius) ?? QUOTE_PACKAGES.find((p) => p.allowsExtraMiles)!;

/** Shared pricing used for display (browser) and the authoritative recalculation (server). */
export function calculateQuote(input: QuoteInput): QuoteResult {
  const radius = Math.max(MIN_RADIUS, Math.min(MAX_RADIUS, Math.round(input.radius)));
  const pkg = packageForRadius(radius);
  const additionalMiles = pkg.allowsExtraMiles ? Math.max(0, radius - pkg.radius) : 0;
  const tier1Miles = Math.min(additionalMiles, 100 - pkg.radius);
  const tier2Miles = Math.max(0, additionalMiles - tier1Miles);
  const additionalRadiusCost = tier1Miles * EXTRA_MILE_RATE + tier2Miles * EXTRA_MILE_RATE_OVER_100;
  const addOnLines: QuoteResult["addOnLines"] = [];
  const customItems: string[] = [];
  const included = new Set(pkg.includedAddOns);
  const newAccountSetup = input.addOns.includes("payments") && input.payment?.account === "new" && !!input.payment?.processor;
  // The $750 new-account service replaces the $500 existing-account integration — never both.
  const purchasedIds = [...new Set(input.addOns)].filter((id) => !included.has(id));
  for (const a of ADD_ONS) {
    if (a.id === "payments" && newAccountSetup) continue;
    if (!purchasedIds.includes(a.id)) continue;
    if (a.oneTime === null) customItems.push(a.name);
    else addOnLines.push({ id: a.id, name: a.name, oneTime: a.oneTime, monthly: a.monthly ?? 0, couponEligible: a.couponEligible });
  }
  const ps = input.paymentSettings ?? DEFAULT_PAYMENT_SETTINGS;
  const pay = input.payment;
  const paymentsRequested = input.addOns.includes("payments");
  const paymentSetupRequired = paymentsRequested && pay?.account === "new" && !!pay.processor;
  if (paymentSetupRequired)
    addOnLines.push({ id: "payment_setup", name: `${PAYMENT_SETUP_SERVICE_NAME} (${PROCESSOR_LABEL[pay!.processor!]})`, oneTime: ps.setupFee, monthly: 0, couponEligible: ps.setupCouponEligible });
  const addOnSubtotal = addOnLines.reduce((s, l) => s + l.oneTime, 0);
  const eligibleSubtotal = addOnLines.filter((l) => l.couponEligible).reduce((s, l) => s + l.oneTime, 0);
  const couponApplied = Math.min(pkg.coupon, eligibleSubtotal);
  const monthlyAddOns = addOnLines.reduce((s, l) => s + l.monthly, 0);
  const packageTotal = pkg.price + additionalRadiusCost;
  const extendedCoverage = radius > EXTENDED_COVERAGE_THRESHOLD;
  const featurePlanRequired = input.addOns.some((id) => FEATURE_PLAN_ADDONS.includes(id));
  // Customer-selected plan, auto-upgraded to the minimum any rule requires.
  const chosen = MONTHLY_PLANS.find((p) => p.key === input.plan) ?? PLAN_HOSTING;
  const minPlan = extendedCoverage ? PLAN_FULL_SUPPORT : featurePlanRequired ? PLAN_HOSTING_MGMT : PLAN_HOSTING;
  const plan = chosen.dollars >= minPlan.dollars ? chosen : minPlan;
  const requiredMonthly = plan.dollars + monthlyAddOns;
  return {
    pkg, radius, includedRadius: pkg.radius, additionalMiles, additionalRadiusCost, packageTotal,
    addOnLines, customItems, addOnSubtotal, eligibleSubtotal,
    coupon: pkg.coupon, couponApplied, couponRemaining: pkg.coupon - couponApplied,
    addOnsAfterCoupon: addOnSubtotal - couponApplied, monthlyAddOns,
    oneTimeTotal: packageTotal + addOnSubtotal - couponApplied,
    monthlyTotal: requiredMonthly,
    areaSqMi: Math.round(Math.PI * radius * radius),
    extendedCoverage, featurePlanRequired, requiredMonthly, plan, planUpgraded: plan.key !== chosen.key,
    includedAddOnIds: pkg.includedAddOns, purchasedAddOnIds: purchasedIds,
    paymentSetupRequired, paymentSetupFee: paymentSetupRequired ? ps.setupFee : 0,
  };
}

export const money = (n: number) => `$${n.toLocaleString("en-US")}`;
